import { EllipsisVertical } from "lucide-react";
import React, { MouseEvent } from "react";
import { useDispatch } from "react-redux";
import { useImmer } from "use-immer";

import { IgIconButton, IgTypography } from "@intelligems/ig-theme";
import { CustomEventDto } from "@intelligems/ig-types";
import { Menu, MenuItem } from "@mui/material";
import { usePutCustomEvent } from "@src/api/custom-events/put-custom-event";
import { UpgradePlanText } from "@src/components/LimitAccess/access-text";
import { useCanCreateCustomEvent } from "@src/components/LimitAccess/check-custom-events-access-hooks";
import { ScopedComponentWithDisable } from "@src/components/LimitAccess/ScopedComponentWithDisable";
import { createCustomEventPayload } from "@src/features/customEvents/helpers";
import {
  setArchivingEventId,
  setUnarchivingEventId,
} from "@src/store/slices/customEvents";
import { setDialog } from "@src/store/slices/global";

interface MoreMenuEventsManagerProps {
  onClose: () => void;
  eventData: CustomEventDto;
  onEditClick: (eventData: CustomEventDto) => void;
}

export const MoreMenuEventsManager = ({
  onClose,
  eventData,
  onEditClick,
}: MoreMenuEventsManagerProps) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useImmer<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const canCreateCustomEvent = useCanCreateCustomEvent();

  const putCustomEvent = usePutCustomEvent({
    renderLocation: "copyEvent",
  });

  const handleCopy = () => {
    const { id, identifier, ...eventWithoutIds } = eventData;
    const eventCopy = {
      ...eventWithoutIds,
      name: "Copy of " + eventWithoutIds.name,
    };
    putCustomEvent.mutate({
      body: {
        customEvent: createCustomEventPayload(eventCopy),
      },
      pathParams: undefined,
      queryParams: undefined,
    });
    handleClose();
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    onClose();
  };

  const onArchiveClick = () => {
    dispatch(setArchivingEventId({ id: eventData.id }));
    dispatch(setDialog({ dialog: "archiveCustomEvent", open: true }));
    handleClose();
  };

  const onUnarchiveClick = () => {
    dispatch(setUnarchivingEventId({ id: eventData.id }));
    dispatch(setDialog({ dialog: "unarchiveCustomEvent", open: true }));
    handleClose();
  };

  return (
    <>
      <IgIconButton
        size={"small"}
        id="more-button"
        aria-controls="more-positioned-menu"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        <EllipsisVertical />
      </IgIconButton>
      <Menu
        id="more-positioned-menu"
        aria-labelledby="more-positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <MenuItem onClick={() => onEditClick(eventData)}>
          <IgTypography>View & Edit</IgTypography>
        </MenuItem>
        {!eventData.archivedAtTs && (
          <ScopedComponentWithDisable
            customDisabledTooltipMessage={UpgradePlanText}
            isEnabled={canCreateCustomEvent}
            debugId={"custom-event-make-a-copy"}
          >
            <MenuItem onClick={handleCopy}>
              <IgTypography>Make a copy</IgTypography>
            </MenuItem>
          </ScopedComponentWithDisable>
        )}
        {eventData.archivedAtTs ? (
          <MenuItem onClick={() => onUnarchiveClick()}>
            <IgTypography>Unarchive</IgTypography>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => onArchiveClick()}>
            <IgTypography>Archive</IgTypography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
